import { useCallback, useEffect, useRef } from 'react';
import { useLocation } from 'react-router';

/**
 * [MCV-1549] Query params carried by notification deep links.
 *
 * Notification URLs are built server-side (mcv_api/services/notification_links.py)
 * and are the only thing pointing a recipient at one row out of many, so the
 * parsing lives in one place rather than being re-implemented per surface. A
 * value that is not a positive integer is treated as absent: these arrive from
 * an email client or a websocket payload and may have been truncated or
 * rewritten in transit, and falling back to the default selection is better
 * than rendering an error for a link that mostly worked.
 */

export const parsePositiveIntParam = (search: string, key: string): number | undefined => {
  const raw = new URLSearchParams(search).get(key);
  if (!raw) return undefined;
  const parsed = Number(raw);
  return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export type DeepLinkedResponse = {
  responseId: number | undefined;
  /**
   * Identifies the *navigation*, not the destination. react-router mints a new
   * key for every navigation, including one to a URL identical to the current
   * one.
   */
  navigationKey: string;
};

/**
 * The `responseId` currently in the URL, plus the navigation that delivered it.
 *
 * Notification rows are react-router `<Link>`s. Clicking a second notification
 * for a route the user is already on changes the search string and nothing
 * else — same pathname, same element, no remount. Anything that reads the
 * param once at mount therefore never sees the new id, leaves the previous
 * conversation selected, and lets the user message or act on the wrong
 * response.
 *
 * The key matters as much as the id. Several notifications about one response
 * are normal — a message, then a status change — and a user who has since
 * clicked another row needs the second one to move them back. Keyed on the id
 * alone, "same id as last time" is indistinguishable from "already handled",
 * and every notification after the first for that response is swallowed.
 */
export const useDeepLinkedResponse = (): DeepLinkedResponse => {
  const location = useLocation();
  return {
    responseId: parsePositiveIntParam(location.search, 'responseId'),
    navigationKey: location.key,
  };
};

/**
 * The one-shot deep-link selection protocol, in one place.
 *
 * Two surfaces manage their own response list and selection —
 * `ProviderJobResponses` and the B2B received view — and both need the same
 * behaviour on arrival:
 *
 *  1. Select the linked row immediately when it is already loaded.
 *  2. Otherwise queue it and ask the caller to reload, because a row that is
 *     absent is not a dead end: it may be newer than the current list, or
 *     hidden by the active status filter. Leaving the user on the previous
 *     conversation while the URL names another is the exact defect this whole
 *     change exists to fix, so "absent" has to mean "go and get it".
 *  3. Act once per navigation, so an ordinary filter reload or post-action
 *     refresh does not drag the user back to a link they have moved past.
 *
 * It lives here rather than being written twice because a divergence between
 * two hand-written copies of one rule is what MCV-1549 was: the notification
 * service and the unmask service each had their own idea of which side a user
 * was on, and they disagreed. The machine-based surfaces get the same protocol
 * from `createOpportunitiesMachine`'s `ON_DEEP_LINK`, which cannot share this
 * hook because its selection lives in actor context rather than React state.
 *
 * `onRequestReload` must reach the target: reset any status filter to its
 * default and refetch. Returning without fetching reintroduces (2).
 */
export const useDeepLinkedResponseSelection = <TRow extends { responseId: number }>({
  rows,
  onSelect,
  onRequestReload,
}: {
  rows: TRow[];
  onSelect: (responseId: number) => void;
  onRequestReload: () => void;
}): {
  consumePendingResponseId: (loaded: TRow[]) => number | undefined;
  /** The queued id, without consuming it: a load asks whether the default list hides it. */
  peekPendingResponseId: () => number | undefined;
} => {
  const { responseId, navigationKey } = useDeepLinkedResponse();
  // Seeded at mount so the very first load consumes the arrival id without
  // depending on which effect runs first, and so the mount navigation is not
  // then re-handled as a fresh one below.
  const pendingRef = useRef<number | undefined>(responseId);
  const lastNavigationRef = useRef<string | undefined>(navigationKey);
  const onSelectRef = useRef(onSelect);
  const onRequestReloadRef = useRef(onRequestReload);

  // Assigned in an effect rather than the render body: a render can be
  // discarded or replayed, so writing refs during one is not safe. Declared
  // before the deep-link effect so that effect always reads current callbacks.
  useEffect(() => {
    onSelectRef.current = onSelect;
    onRequestReloadRef.current = onRequestReload;
  }, [onSelect, onRequestReload]);

  useEffect(() => {
    if (responseId == null) {
      // Navigating to a URL with no responseId drops any queued one. A reload
      // requested for a row that was missing can still be in flight, and the
      // URL is the authority on what should be open — letting the queued id
      // survive would have that reload select a response the user has left.
      pendingRef.current = undefined;
      return;
    }
    if (navigationKey === lastNavigationRef.current) return;
    lastNavigationRef.current = navigationKey;

    // Queued unconditionally, including when the row is already on screen.
    // `rows` can be stale: a filter load started before this navigation may
    // still be in flight, and when it lands without the target the caller
    // would fall back to its first row while the URL still names the target.
    // Keeping the id queued makes that load select it instead.
    pendingRef.current = responseId;

    if (rows.some((row) => row.responseId === responseId)) {
      // Optimistic — move the UI now rather than after a round-trip. The
      // reload below is what makes it authoritative.
      onSelectRef.current(responseId);
    }

    // Requested in every case, not just the not-found one. A ref write
    // schedules nothing on its own, and an explicit deep link has to outrank
    // and supersede whatever load is in flight — otherwise an earlier filtered
    // request decides what the user ends up looking at.
    onRequestReloadRef.current();
  }, [responseId, navigationKey, rows]);

  const consumePendingResponseId = useCallback((loaded: TRow[]) => {
    const requested = pendingRef.current;
    pendingRef.current = undefined;
    return requested != null && loaded.some((row) => row.responseId === requested)
      ? requested
      : undefined;
  }, []);

  const peekPendingResponseId = useCallback(() => pendingRef.current, []);

  return { consumePendingResponseId, peekPendingResponseId };
};
